import React from 'react';
import GPUItem from './GPUItem';
import { parseCardSeries, computePriceChange30Days } from './gpuUtils';

const GPUBrowserTable = ({
    gpus,
    sortConfig,
    setSortConfig,
    searchTerm,
    selectedManufacturers,
    selectedCardSeries,
    selectedBrands,
    priceRange
}) => {
    // Sorting helper: for card series sorting, we extract baseNumber and variantScore.
    const getSortableValue = (gpu, key) => {
        switch (key) {
            case 'title':
                return gpu.title ? gpu.title.toLowerCase() : '';
            case 'cardseries': {
                const { baseNumber, variantScore } = parseCardSeries(gpu.cardseries || '');
                return baseNumber * 100 + variantScore;
            }
            case 'manufacturer':
                return gpu.manufacturer ? gpu.manufacturer.toLowerCase() : '';
            case 'current_price':
                return gpu.current_price !== null ? gpu.current_price : Number.MAX_SAFE_INTEGER;
            case 'base_stock':
                return gpu.base_stock !== null ? gpu.base_stock : Number.MAX_SAFE_INTEGER;
            case 'price_history': {
                const prices =
                    gpu.price_history && gpu.price_history.Amazon
                        ? gpu.price_history.Amazon.map(record => record.price).filter(price => price !== null)
                        : [];
                return prices.length > 0 ? prices[prices.length - 1] : Number.MAX_SAFE_INTEGER;
            }
            case 'price_change_30': {
                return computePriceChange30Days(gpu.price_history);
            }
            case 'best_used_price':
                return gpu.best_used_price !== null ? gpu.best_used_price : Number.MAX_SAFE_INTEGER;
            default:
                return '';
        }
    };

    const handleSort = (key) => {
        setSortConfig(prev => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
    };

    const renderSortArrow = (key) => {
        if (sortConfig.key === key) {
            return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
        }
        return '';
    };

    // Filter GPUs
    const filteredGPUs = gpus.filter(gpu => {
        const searchLower = searchTerm.toLowerCase();
        if (
            searchTerm &&
            !gpu.title.toLowerCase().includes(searchLower) &&
            !gpu.cardseries.toLowerCase().includes(searchLower)
        )
            return false;
        if (selectedManufacturers.length > 0 && !selectedManufacturers.includes(gpu.manufacturer))
            return false;
        if (selectedCardSeries.length > 0 && !selectedCardSeries.includes(gpu.cardseries))
            return false;
        if (selectedBrands.length > 0 && !selectedBrands.includes(gpu.brand))
            return false;
        if (gpu.current_price !== null && gpu.current_price < priceRange[0])
            return false;
        if (gpu.current_price !== null && gpu.current_price > priceRange[1])
            return false;
        return true;
    });

    // Sort GPUs based on sortConfig.
    const sortedGPUs = [...filteredGPUs].sort((a, b) => {
        if (!sortConfig.key) return 0;
        const aValue = getSortableValue(a, sortConfig.key);
        const bValue = getSortableValue(b, sortConfig.key);
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    return (
        <>
            <table className="w-full table-auto border-collapse border border-gray-300 shadow-lg text-sm">
                <thead>
                    <tr className="bg-gray-200">
                        <th
                            className="border border-gray-300 px-2 py-2 cursor-pointer select-none"
                            onClick={() => handleSort('title')}
                        >
                            Title{renderSortArrow('title')}
                        </th>
                        <th
                            className="border border-gray-300 px-2 py-2 cursor-pointer select-none"
                            onClick={() => handleSort('cardseries')}
                        >
                            Card Series{renderSortArrow('cardseries')}
                        </th>
                        <th
                            className="border border-gray-300 px-2 py-2 cursor-pointer select-none"
                            onClick={() => handleSort('manufacturer')}
                        >
                            Manufacturer{renderSortArrow('manufacturer')}
                        </th>
                        <th
                            className="border border-gray-300 px-2 py-2 cursor-pointer select-none"
                            onClick={() => handleSort('current_price')}
                        >
                            Listing Price{renderSortArrow('current_price')}
                        </th>
                        <th
                            className="border border-gray-300 px-2 py-2 cursor-pointer select-none"
                            onClick={() => handleSort('base_stock')}
                        >
                            Stock (Estimate){renderSortArrow('base_stock')}
                        </th>
                        <th
                            className="border border-gray-300 px-2 py-2"
                        >
                            Price History
                        </th>
                        <th
                            className="border border-gray-300 px-2 py-2 cursor-pointer select-none"
                            onClick={() => handleSort('price_change_30')}
                        >
                            % Change (30 days){renderSortArrow('price_change_30')}
                        </th>
                        <th
                            className="border border-gray-300 px-2 py-2 cursor-pointer select-none"
                            onClick={() => handleSort('best_used_price')}
                        >
                            3rd Party Sellers{renderSortArrow('best_used_price')}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {sortedGPUs.map(gpu => (
                        <GPUItem key={gpu.id} gpu={gpu} />
                    ))}
                </tbody>
            </table>
            {sortedGPUs.length === 0 && (
                <div className="text-center text-gray-600 mt-4">No products found.</div>
            )}
        </>
    );
};

export default GPUBrowserTable;
